import { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { MultiSelect } from "primereact/multiselect";
import { FaTrash } from "react-icons/fa";
import CategoryService from "../../../../services/CategoryService";
import AlertService from "../../../../services/AlertService";
import AuthContext from "../../../../auth/AuthContext";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";

const AddCategoryModal = ({
  show,
  handleClose,
  isEditMode,
  initialCategory,
  reloadList,
}) => {
  const authContext = useContext(AuthContext);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [category, setCategory] = useState("");
  const [selectedUnder, setSelectedUnder] = useState([]);
  const [underOptions, setUnderOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState(0);
  const submitControllerRef = useRef(null);


  useEffect(() => {
    if (isEditMode && initialCategory) {
      setCategory(initialCategory.categoryName || "");
      setSelectedUnder(initialCategory.children || []);
      setId(initialCategory.id || 0);
    } else {
      resetForm();
    }
  }, [isEditMode, initialCategory, show]);

const loadAllCategories = useCallback(async (signal) => { 
    try {
      const data = await CategoryService.GetAllCategories(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const options = data.data.all.map((m) => ({
          label: m.categoryName,
          value: m.id,
        }));
        setUnderOptions(options);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadAllCategories(controller.signal); 
    return () => controller.abort(); 
  }, [loadAllCategories]);

  const resetForm = () => {
    setCategory("");
    setSelectedUnder([]);
    setErrors({});
    setId(0);
  };

  const onClose = () => {
    resetForm();
    handleClose();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!category.trim()) newErrors.category = "Please enter category";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



const handleSubmit = async () => {
  if (!validateForm()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  const payload = {
    categoryName: category,
    children: selectedUnder,
  };
  if (isEditMode && id > 0) {
    payload.id = id;
  }

  try {
    const data = await CategoryService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("Category saved successfully!");
      resetForm();
      handleClose();
      reloadList();
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("Failed to save category");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  const handleDelete = async () => {
    if (id > 0) {
      const data = await CategoryService.Remove(id, authContext);
      if (data.isSuccess) {
        AlertService.success("Category deleted successfully!");
        resetForm();
        reloadList();
      } else {
        AlertService.error(data.errorMessage);
      }
    }
  };


  return (
    <>
      <Modal show={show} onHide={onClose} backdrop="static" centered size="xl" enforceFocus={false}>
        <Modal.Header closeButton className="border-0 pt-3">
          <Modal.Title>{isEditMode ? "Edit Category" : "Add New Category"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Control
                type="text"
                className="custom-input custom-input-color"
                placeholder="Enter category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
              />
              {errors.category && <div className="text-danger">{errors.category}</div>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Under</Form.Label>
              <MultiSelect
                value={selectedUnder}
                options={underOptions.filter((opt) => opt.value !== id)}
                onChange={(e) => setSelectedUnder(e.value)}
                placeholder="Select options"
                display="chip"
                className="w-100 custom-input-color"
                filter
                disabled={loading}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 pb-4 px-4">
          <div className="d-flex justify-content-between w-100 flex-wrap gap-3">
            {/* Delete only in edit mode */}
            {isEditMode && (
              <div
                className="text-danger d-flex align-items-center"
                onClick={() => {
                  handleClose();
                  setTimeout(() => {
                    setShowDeleteDialog(true);
                  }, 300);
                }}
                style={{ fontWeight: 500, cursor: "pointer" }}
              >
                <FaTrash className="me-2" size={20} />
                Delete
              </div>
            )}

            {/* Submit/Cancel section - always on right side */}
            <div className="ms-auto d-flex gap-3">
              <Button
                variant="outline-secondary"
                className="px-5 py-2"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="px-5 py-2"
                onClick={handleSubmit}
                disabled={loading}
              >
                {isEditMode ? "Update" : "Submit"}
              </Button>
            </div>
          </div>
        </Modal.Footer>


      </Modal>
      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          setShowDeleteDialog(false);
          await handleDelete();
        }}
        title={
          <>
            Are you sure you want to delete the category{" "}
            <strong>{category}</strong>?
          </>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />

    </>
  );
};

export default AddCategoryModal;
